import React from 'react';
import { Tabs, useRouter } from 'expo-router';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Dimensions, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { spacing, useThemePalette } from '../theme';

type IconName = keyof typeof MaterialCommunityIcons.glyphMap;

const ICONS: Record<string, [IconName, IconName]> = {
  index: ['briefcase', 'briefcase-outline'],
  profile: ['account', 'account-outline'],
  favorites: ['heart', 'heart-outline'],
  applications: ['clipboard-text', 'clipboard-text-outline'],
  'resume-builder': ['file-document-edit', 'file-document-edit-outline'],
};

export default function TabLayout() {
  const { t } = useTranslation();
  const router = useRouter();
  const palette = useThemePalette();
  const { width } = Dimensions.get('window');
  const isWeb = Platform.OS === 'web';
  const isDesktop = isWeb && width > 1024;

  if (isDesktop) {
    return (
      <View style={[styles.desktopContainer, { backgroundColor: palette.canvas }]}>
        <View style={[styles.sidebar, { backgroundColor: palette.navBar }]}>
          <TouchableOpacity style={styles.navLink} onPress={() => router.push('/')}>
            <MaterialCommunityIcons name="briefcase-outline" size={22} color={palette.textOnDark} />
            <Text style={[styles.navText, { color: palette.textOnDark }]}>{t('tabs.allJobs')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.navLink} onPress={() => router.push('/applications')}>
            <MaterialCommunityIcons name="clipboard-text-outline" size={22} color={palette.textOnDark} />
            <Text style={[styles.navText, { color: palette.textOnDark }]}>{t('tabs.applications')}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.navLink} onPress={() => router.push('/profile')}>
            <MaterialCommunityIcons name="account-outline" size={22} color={palette.textOnDark} />
            <Text style={[styles.navText, { color: palette.textOnDark }]}>{t('tabs.profile')}</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.content}>
          <Tabs
            screenOptions={{
              headerShown: false,
              tabBarStyle: { display: 'none' },
            }}
          >
            <Tabs.Screen name="index" />
            <Tabs.Screen name="applications" />
            <Tabs.Screen name="profile" />
            <Tabs.Screen name="favorites" options={{ href: null }} />
            <Tabs.Screen name="alerts" options={{ href: null }} />
            <Tabs.Screen name="resume-builder" options={{ href: null }} />
          </Tabs>
        </View>
      </View>
    );
  }

  return (
    <Tabs
      screenOptions={({ route }) => ({
        tabBarIcon: ({ color, focused }) => {
          const [active, inactive] = ICONS[route.name] ?? ICONS.index;
          return (
            <MaterialCommunityIcons
              name={focused ? active : inactive}
              size={24}
              color={color}
            />
          );
        },
        // Show the label only for the focused tab; unfocused tabs are icon-only.
        tabBarLabel: ({ focused, color, children }) =>
          focused ? (
            <Text style={[styles.tabLabel, { color }]} numberOfLines={1}>
              {children}
            </Text>
          ) : null,
        tabBarActiveTintColor: palette.primary,
        tabBarInactiveTintColor: palette.textOnDarkMuted,
        tabBarStyle: {
          backgroundColor: palette.navBar,
          borderTopColor: palette.navBarBorder,
          borderTopWidth: StyleSheet.hairlineWidth,
          // RN-Web below 768px renders the icon-above-label layout, but
          // react-navigation's bar View still has the default 49px height —
          // the label overflows upward and visually clips the list above.
          // Pinning a taller height contains the content within the bar.
          ...(Platform.OS === 'web' ? { height: 64 } : {}),
        },
        tabBarShowLabel: true,
        headerShown: false,
      })}
    >
      <Tabs.Screen name="index" options={{ title: t('tabs.allJobs') }} />
      <Tabs.Screen name="applications" options={{ title: t('tabs.applications') }} />
      <Tabs.Screen
        name="resume-builder"
        options={isWeb ? { href: null } : { title: t('tabs.resumeBuilder') }}
      />
      <Tabs.Screen name="profile" options={{ title: t('tabs.profile') }} />
      <Tabs.Screen name="favorites" options={{ href: null }} />
      <Tabs.Screen name="alerts" options={{ href: null }} />
    </Tabs>
  );
}

const styles = StyleSheet.create({
  desktopContainer: { flex: 1, flexDirection: 'row' },
  sidebar: {
    width: 220,
    paddingTop: spacing.lg,
    paddingHorizontal: spacing.sm,
  },
  navLink: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    gap: spacing.sm,
  },
  navText: { fontSize: 15 },
  content: { flex: 1 },
  tabLabel: { fontSize: 12, fontWeight: '600' },
});
